import { Button, Flex, Pill, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { EvaluationReport } from '@/module/Evaluation/model/EvaluationTypes';

import { Content } from '@/core/components';

import { useAppNotification } from '@/feature/utils';
import { useCurrentUser } from '@feature/security';

import { useEvaluationEditValidationDates } from '../model/EvaluationQuery';
import { EvaluationDatesSchema, defaultEvaluationDatesValues, evaluationDatesSchema } from '../model/EvaluationSchema';
import { EvaluationChangeValidationDatesForm } from './EvaluationChangeValidationDatesForm';

export interface EvaluationHeaderProps {
  evaluation: EvaluationReport;
  hideSummary?: boolean;
}

const convert = (evaluationReport: EvaluationReport): EvaluationDatesSchema => ({
  ...evaluationReport,
  closingDate: evaluationReport.closingDate,
  headOfDepartmentClosingDate: evaluationReport.headOfDepartmentClosingDate,
  headOfDisciplineClosingDate: evaluationReport.headOfDisciplineClosingDate,
});

export function EvaluationHeader({ evaluation, hideSummary }: EvaluationHeaderProps) {
  const { currentRole } = useCurrentUser();
  const { mutate, isPending, isError, isSuccess } = useEvaluationEditValidationDates(evaluation.id);
  const hasAccess = ['DEPARTMENT_ADMIN', 'FACULTY_ADMIN', 'UNIVERSITY_ADMIN'].indexOf(currentRole) !== -1;
  const notification = useAppNotification();
  const [opened, { open, close }] = useDisclosure(false);

  const handleChange = (values: EvaluationDatesSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: () => {
        notification.success();
        close();
      },
    });
  };

  const navigate = useNavigate();
  const values = useMemo(() => convert(evaluation), [evaluation]);

  return (
    <Content type="full">
      <Flex
        justify="center"
        align="center"
        gap="4px"
      >
        <Text fw="500">{evaluation.title}</Text>
        <Pill bg={evaluation.open ? 'green' : 'red'}>{evaluation.open ? 'Deschisă' : 'Închisă'}</Pill>
      </Flex>
      {hasAccess && !hideSummary && (
        <Flex
          justify="center"
          align="center"
          gap="10px"
        >
          <Button
            component={Link}
            to="department-summary-results"
            size="xs"
          >
            Rezultate centralizate pe departamente
          </Button>
          <Button
            component={Link}
            to="faculty-summary-results"
            size="xs"
          >
            Rezultate centralizate pe facultăți
          </Button>
          <Button
            size="xs"
            variant="outline"
            loading={isPending}
            onClick={open}
          >
            Editează interval de introducere date / validare
          </Button>
          {opened && (
            <EvaluationChangeValidationDatesForm
              onClose={close}
              isSubmitting={isPending}
              schema={evaluationDatesSchema}
              defaultValues={defaultEvaluationDatesValues}
              isError={isError}
              isSuccess={isSuccess}
              values={values}
              onSubmit={handleChange}
              onCancel={() => navigate({ to: '..' })}
            />
          )}
        </Flex>
      )}
    </Content>
  );
}
